// Account settings (/settings/account, docs/06 §2, docs/03 §3a/§3b): display
// name, passkeys, account password set/change/remove, safety-number viewer,
// blocked users, and Delete account with the docs/03 §3b confirmation flow
// (key signature + password if set + typing your UUID). Server-backed — reached
// only once the device has a registered userId.

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import type { Friend, MeResponse } from '@merkury/protocol';
import { useAccount } from '../../shell/accountContext.js';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { candidateSeed } from '../../avatar/mercury.js';
import { seedToPhrase, type IdentityKeys } from '../../identity/index.js';
import { formatSafetyNumber } from '@merkury/brain/call/crypto/safetyNumber.js';
import { useT } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';
import { AvatarPicker } from '../ui/AvatarPicker.js';
import { Section, SettingsSubScreen, buttonClass, inputClass, quietButtonClass } from './chrome.js';
import { useSignOutFlow, SignOutWarning } from './signOutFlow.js';

// --- Recovery phrase ------------------------------------------------------------
// Exported: the Settings aggregator renders it as a fallback for devices that
// haven't registered yet (no userId → no Account row), so the phrase stays
// reachable offline and pre-registration (prime directive).

export function PhraseSection({ identity }: { identity: IdentityKeys }): ReactNode {
  const t = useT();
  const [revealed, setRevealed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Section title={t('settings.recoveryPhrase')}>
      {!revealed ? (
        <>
          <p className="text-xs text-muted">{t('settings.recoveryWarn')}</p>
          <label className="flex min-h-touch items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="h-5 w-5"
            />
            {t('settings.nobodyWatching')}
          </label>
          <button
            type="button"
            disabled={!confirmed}
            onClick={() => setRevealed(true)}
            className={buttonClass}
          >
            {t('settings.reveal')}
          </button>
        </>
      ) : (
        <>
          <ol
            data-testid="recovery-phrase"
            className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-xl bg-raised p-3 sm:grid-cols-3"
          >
            {seedToPhrase(identity.seed)
              .split(' ')
              .map((word, i) => (
                <li key={i} className="flex items-baseline gap-2 text-sm">
                  <span aria-hidden="true" className="w-5 text-right text-muted">
                    {i + 1}.
                  </span>
                  <span className="select-text font-mono text-ink">{word}</span>
                </li>
              ))}
          </ol>
          <button type="button" onClick={() => setRevealed(false)} className={quietButtonClass}>
            {t('settings.hide')}
          </button>
        </>
      )}
    </Section>
  );
}

// --- Display name -------------------------------------------------------------

function DisplayNameSection({
  me,
  onSaved,
}: {
  me: MeResponse | null;
  onSaved: (me: MeResponse) => void;
}): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (me) setName(me.displayName);
  }, [me]);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed.length > 64) return;
    const updated = await api.updateMe({ displayName: trimmed }).catch(() => null);
    if (updated) {
      onSaved(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  }

  return (
    <Section title={t('account.displayName')}>
      <form className="flex gap-2" onSubmit={handleSubmit}>
        <label className="flex-1">
          <span className="sr-only">{t('account.displayName')}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={64}
            className={`w-full ${inputClass}`}
          />
        </label>
        <button type="submit" className={buttonClass}>
          {saved ? t('account.saved') : t('common.save')}
        </button>
      </form>
    </Section>
  );
}

// --- Profile picture ------------------------------------------------------------
// The avatar is deterministic: f(userId, seed) where seed is a candidate index
// 0..8 (avatar/mercury.ts). Nine candidates over three pages of three; "Show
// more" cycles the pages (wrapping). Selecting PATCHes /me and updates the
// screen optimistically, reverting with a notice if the server refuses.

function ProfilePictureSection({
  me,
  userId,
  onSaved,
}: {
  me: MeResponse | null;
  userId: string;
  onSaved: (me: MeResponse) => void;
}): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const [error, setError] = useState<string | null>(null);
  const currentSeed = me?.avatarSeed;

  async function select(index: number): Promise<void> {
    setError(null);
    if (me) onSaved({ ...me, avatarSeed: index }); // optimistic
    try {
      await api.updateMe({ avatarSeed: index });
    } catch {
      if (me) onSaved(me); // revert
      setError(t('account.couldNotUpdateAvatar'));
    }
  }

  return (
    <Section title={t('account.profilePicture')}>
      <p className="text-xs text-muted">{t('account.profilePictureHelp')}</p>
      {currentSeed !== undefined ? (
        <div className="flex items-center gap-3">
          <MercuryAvatar seed={candidateSeed(userId, currentSeed)} size={64} className="rounded-full" />
          <span className="text-sm text-muted">{t('account.avatarCurrent')}</span>
        </div>
      ) : (
        <p className="text-xs text-muted">{t('account.noPictureYet')}</p>
      )}
      <AvatarPicker
        userId={userId}
        {...(currentSeed !== undefined ? { value: currentSeed } : {})}
        onChange={(index) => void select(index)}
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </Section>
  );
}

// --- Safety numbers -------------------------------------------------------------

function SafetyNumbersSection({ friends }: { friends: Friend[] }): ReactNode {
  const t = useT();
  const { engine } = useScreens();
  const [selected, setSelected] = useState('');
  const [rendered, setRendered] = useState<string | null>(null);

  async function handleShow(userId: string): Promise<void> {
    setSelected(userId);
    setRendered(null);
    if (!userId) return;
    const sn = await engine.safetyNumberWith(userId);
    setRendered(sn ? formatSafetyNumber(sn) : t('account.unavailableKey'));
  }

  return (
    <Section title={t('account.safetyNumbers')}>
      <p className="text-xs text-muted">{t('account.safetyHelp')}</p>
      <label className="flex flex-col gap-1 text-sm text-ink">
        {t('account.contact')}
        <select
          value={selected}
          onChange={(e) => void handleShow(e.target.value)}
          className={inputClass}
        >
          <option value="">{t('account.chooseContact')}</option>
          {friends.map((f) => (
            <option key={f.userId} value={f.userId}>
              {f.displayName}
            </option>
          ))}
        </select>
      </label>
      {rendered && (
        <p data-testid="safety-number" className="rounded-xl bg-raised p-3 text-center text-lg">
          {rendered}
        </p>
      )}
    </Section>
  );
}

// --- Password -------------------------------------------------------------------

// No `identity`/`userId`: the brain holds both, signs the guard message itself,
// and persists or clears the password authKey (2FA proof) as part of the
// ceremony — so this section no longer touches a secret at all.
export function PasswordSection({
  me,
  onChanged,
}: {
  me: MeResponse | null;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const { credentials } = useAccount();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [terminateOthers, setTerminateOthers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasPassword = me?.hasPassword ?? false;

  async function handleSet(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (password.length < 8) {
      setError(t('account.useAtLeast8'));
      return;
    }
    if (password !== confirm) {
      setError(t('account.passwordsDontMatch'));
      return;
    }
    setBusy(true);
    try {
      await credentials.setPassword(api, password, terminateOthers);
      setMessage(hasPassword ? t('account.passwordChanged') : t('account.passwordSet'));
      setPassword('');
      setConfirm('');
      onChanged();
    } catch {
      setError(t('account.couldNotUpdatePassword'));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(): Promise<void> {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      // Not `terminateOthers`: that checkbox lives inside the set/change form
      // above and reads as a choice about THAT submission. Carrying a stale tick
      // over to Remove would silently sign the user's other devices out.
      await credentials.removePassword(api, false);
      setMessage(t('account.passwordRemoved'));
      onChanged();
    } catch {
      setError(t('account.couldNotRemovePassword'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title={t('account.password')}>
      <p className="text-xs text-muted">
        {hasPassword ? t('account.passwordSetHelp') : t('account.passwordOptionalHelp')}
      </p>
      <form className="flex flex-col gap-2" onSubmit={handleSet}>
        <label className="flex flex-col gap-1 text-sm text-ink">
          {hasPassword ? t('account.newPassword') : t('account.passwordLabel')}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm text-ink">
          {t('account.confirmPassword')}
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className={inputClass}
          />
        </label>
        <label className="flex min-h-touch items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={terminateOthers}
            onChange={(e) => setTerminateOthers(e.target.checked)}
            className="h-5 w-5"
          />
          {t('account.signOutOthers')}
        </label>
        <button type="submit" disabled={busy} className={buttonClass}>
          {hasPassword ? t('account.changePassword') : t('account.setPassword')}
        </button>
      </form>
      {hasPassword && (
        <button
          type="button"
          disabled={busy}
          onClick={() => void handleRemove()}
          className={quietButtonClass}
        >
          {t('account.removePassword')}
        </button>
      )}
      {message && (
        <p role="status" className="text-sm text-ok">
          {message}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </Section>
  );
}

// --- Blocked users -------------------------------------------------------------------

function BlockedSection(): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const [blocked, setBlocked] = useState<string[]>([]);

  const reload = useCallback(async () => {
    const res = await api.listBlocks().catch(() => null);
    if (res) setBlocked(res.blocked);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <Section title={t('account.blockedUsers')}>
      {blocked.length === 0 && <p className="text-xs text-muted">{t('account.nobodyBlocked')}</p>}
      <ul className="flex flex-col gap-2">
        {blocked.map((userId) => (
          <li key={userId} className="flex items-center gap-3 rounded-xl bg-raised p-3">
            <span className="flex-1 break-all font-mono text-xs text-ink">{userId}</span>
            <button
              type="button"
              aria-label={t('account.unblockAria', { userId })}
              onClick={async () => {
                await api.unblock(userId).catch(() => undefined);
                await reload();
              }}
              className={quietButtonClass}
            >
              {t('account.unblock')}
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// --- Log out ------------------------------------------------------------------------------------
// Signing out wipes the local identity + session (accountContext.signOut →
// AccountService.signOut). That is only safe because the identity can be
// restored afterwards — by a synced passkey (docs/03 §2 PRF), the recovery
// phrase, or the account password — so the confirmation says exactly that
// before wiping. Distinct from Delete account, which destroys the account.
// The confirm/wipe/navigate mechanics live in signOutFlow.ts — terminating
// the CURRENT session from the Sessions screen is the same action under a
// different trigger, and reuses it rather than duplicating it.

function LogOutSection(): ReactNode {
  const t = useT();
  const flow = useSignOutFlow();

  return (
    <Section title={t('account.signOut')}>
      <p className="text-xs text-muted">{t('account.signOutHelp')}</p>
      {!flow.confirming ? (
        <button type="button" onClick={flow.requestConfirm} className={quietButtonClass}>
          {t('account.signOut')}
        </button>
      ) : (
        <SignOutWarning flow={flow} />
      )}
    </Section>
  );
}

// --- Delete account ---------------------------------------------------------------------------

function DeleteAccountSection({
  me,
  userId,
}: {
  me: MeResponse | null;
  userId: string;
}): ReactNode {
  const t = useT();
  const { api } = useScreens();
  const { credentials, signOut } = useAccount();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typedUuid, setTypedUuid] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasPassword = me?.hasPassword ?? false;
  const uuidMatches = typedUuid.trim().toLowerCase() === userId.toLowerCase();

  async function handleDelete(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!uuidMatches || busy) return;
    setError(null);
    setBusy(true);
    try {
      // The brain wipes local state itself; signOut() after it is harmless and
      // idempotent, and keeps the UI transition (→ /onboarding) unchanged.
      await credentials.deleteAccount(api, hasPassword ? password : undefined);
      await signOut();
      void navigate('/onboarding', { replace: true });
    } catch {
      setError(t('account.deletionFailed'));
      setBusy(false);
    }
  }

  return (
    <Section title={t('account.deleteAccount')}>
      <p className="text-xs text-muted">{t('account.deleteHelp')}</p>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="min-h-touch rounded-xl bg-danger px-4 text-sm font-medium text-ink"
        >
          {t('account.deleteOpen')}
        </button>
      ) : (
        <form className="flex flex-col gap-2" onSubmit={handleDelete}>
          <label className="flex flex-col gap-1 text-sm text-ink">
            {t('account.typeIdConfirm', { userId })}
            <input
              type="text"
              value={typedUuid}
              onChange={(e) => setTypedUuid(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className={`font-mono ${inputClass}`}
            />
          </label>
          {hasPassword && (
            <label className="flex flex-col gap-1 text-sm text-ink">
              {t('account.yourPassword')}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className={inputClass}
              />
            </label>
          )}
          <button
            type="submit"
            disabled={!uuidMatches || busy || (hasPassword && password === '')}
            className="min-h-touch rounded-xl bg-danger px-4 text-sm font-medium text-ink disabled:opacity-40"
          >
            {busy ? t('account.deleting') : t('account.permanentlyDelete')}
          </button>
          <button type="button" onClick={() => setOpen(false)} className={quietButtonClass}>
            {t('common.cancel')}
          </button>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
        </form>
      )}
    </Section>
  );
}

// --- Screen ------------------------------------------------------------------------------------

export function AccountSettingsScreen(): ReactNode {
  const t = useT();
  const { state } = useAccount();
  const { api } = useScreens();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);

  const reloadMe = useCallback(async () => {
    const res = await api.getMe().catch(() => null);
    if (res) setMe(res);
  }, [api]);

  useEffect(() => {
    void reloadMe();
    void api
      .listFriends()
      .then((res) => setFriends(res.friends))
      .catch(() => undefined);
  }, [api, reloadMe]);

  if (state.status !== 'ready') {
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app items-center justify-center p-6">
        <p className="text-sm text-muted" role="status">
          {t('common.loading')}
        </p>
      </main>
    );
  }
  const userId = state.account.userId;
  // Account settings are server-backed; an unregistered device has no userId and
  // is bounced back to Settings (its "Account" row is hidden there anyway).
  if (!userId) return <Navigate to="/settings" replace />;

  return (
    <SettingsSubScreen section="account" title={t('account.title')}>
      <DisplayNameSection me={me} onSaved={setMe} />
      <ProfilePictureSection me={me} userId={userId} onSaved={setMe} />
      {/* Passkeys, password and recovery phrase moved to Settings › Security. */}
      <SafetyNumbersSection friends={friends} />
      <BlockedSection />
      <LogOutSection />
      <DeleteAccountSection me={me} userId={userId} />
    </SettingsSubScreen>
  );
}
